import React, { useEffect } from 'react';
import { useCacheStore, useAuthStore } from '@/stores';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export const useCacheWarming = () => {
  const { warmCache, preload } = useCacheStore();
  const { user } = useAuthStore();
  const componentLogger = logger.createComponentLogger('CacheWarming');

  // Define critical data to preload
  const criticalCacheEntries = React.useMemo(() => {
    if (!user) return [];

    return [
      {
        key: `user-profile-${user.id}`,
        fetcher: async () => {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();
          if (error) throw error;
          return data;
        },
        staleTime: 30 * 60 * 1000, // 30 minutes
      },
      {
        key: `recent-activities-${user.id}`,
        fetcher: async () => {
          const [runningData, fitnessData] = await Promise.all([
            supabase
              .from('running_performance')
              .select('id, recorded_date, distance_meters, pace_per_km_seconds')
              .eq('user_id', user.id)
              .order('recorded_date', { ascending: false })
              .limit(5),
            supabase
              .from('general_fitness')
              .select('id, recorded_date, activity_type, duration_seconds')
              .eq('user_id', user.id)
              .order('recorded_date', { ascending: false })
              .limit(5)
          ]);
          
          return {
            running: runningData.data || [],
            fitness: fitnessData.data || []
          };
        },
        staleTime: 10 * 60 * 1000, // 10 minutes
      },
      {
        key: `training-stats-${user.id}`,
        fetcher: async () => {
          const { data, error } = await supabase.rpc('get_coaching_insights', {
            p_user_id: user.id,
            p_weeks_back: 4
          });
          if (error) throw error;
          return data;
        },
        staleTime: 20 * 60 * 1000, // 20 minutes
      }
    ];
  }, [user]);

  // Warm cache on user authentication
  useEffect(() => {
    if (user && criticalCacheEntries.length > 0) {
      componentLogger.debug('Starting cache warming for authenticated user');
      
      warmCache(criticalCacheEntries)
        .then(() => {
          componentLogger.info('Cache warming completed successfully');
        })
        .catch((error) => {
          componentLogger.error('Cache warming failed', error);
        });
    }
  }, [user, warmCache, criticalCacheEntries, componentLogger]);

  // Background refresh for stale data
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(() => {
      // Preload stale critical data in background
      criticalCacheEntries.forEach(({ key, fetcher, staleTime }) => {
        preload(key, fetcher, staleTime).catch((error) => {
          componentLogger.warn(`Background refresh failed for ${key}`, error);
        });
      });
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(refreshInterval);
  }, [user, criticalCacheEntries, preload, componentLogger]);

  return {
    warmCache: () => warmCache(criticalCacheEntries),
    preloadCriticalData: async () => {
      for (const entry of criticalCacheEntries) {
        await preload(entry.key, entry.fetcher, entry.staleTime);
      }
    }
  };
};